import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isNaN from 'lodash/isNaN';
import sortBy from 'lodash/sortBy';
import head from 'lodash/head';
import last from 'lodash/last';
import find from 'lodash/find';
import capitalize from 'lodash/capitalize';
import isArray from 'lodash/isArray';
import moment from 'moment';
import { saveAs } from 'file-saver';
import {
  API,
  OrderTypeHelper,
  StorageUtil,
  GlobalActions,
  Notes,
  ToastActions,
  Tag,
  Submenu,
  VibeAccordion,
  VibeAlert,
  VibeButton,
  VibeIcon,
  viDownload,
  viEye,
  color,
  VibeModal,
} from 'vibeguide';
import { styled } from '@mui/material/styles';
import Grid from '@mui/material/GridLegacy';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import SubNavigation from '../../../Header/SubNavigation';
import SegmentSchedule from './SegmentSchedule';
import SegmentInformation from './SegmentInformation';
import InfoCards from '../OrderDashboard/InfoCards/InfoCards';
import OrderCreatives from './OrderCreatives';
import OrderSegmentAllocationOverview from './OrderSegmentAllocationOverview';
import './OrderSegment.scss';

const BootstrapButton = styled(Button)({
  border: `1px solid ${color.manatee}`,
  color: color.gray700,
  textTransform: 'none',
  fontFamily: [
    '-apple-system',
    'Lato',
    'sans-serif"',
  ].join(','),
});

function OrderSegment({
  user,
  setPanel,
  queueToast,
}) {
  const [data, setData] = useState({});
  const [campaignValueAdjustments, setCampaignValueAdjustments] = useState([]);
  const [editCampaignValueAdjustments, setEditCampaignValueAdjustments] = useState([{
    date: '',
    spend: 0,
    changed: false,
  }]);
  const [campaignChanged, setCampaignChanged] = useState(false);
  const [accordionExpanded, setAccordionExpanded] = useState(StorageUtil.getLocal('orders:expanded', true));
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(false);
  const [APISavingError, setAPISavingError] = useState(false);

  /**
   * Accepted Views: "impression" or "spot"
   * default to impression, automatically set to spot after order is fetched if criteria is met
   */
  const [currentView, setCurrentView] = useState('impression');

  const {
    id: orderId,
    segmentId,
    type,
  } = useParams();

  const {
    // order,
    segment,
  } = data;

  // is the current view impression or spot based?
  const isImpression = currentView === 'impression';

  const updateTitle = (status) => {
    return `${capitalize(status)} Orders`;
  };

  /**
   * When the Segment Information Accordion is changed
   */
  const onChangeAccordion = (e, expanded) => {
    StorageUtil.setLocal('orders:expanded', expanded);
    setAccordionExpanded(expanded);
  };

  const getData = async () => {
    if (!segmentId || !orderId) {
      return;
    }

    const newData = {};

    // always get segment details
    newData.segment = await API.Order.Segment.get({
      _id: orderId,
      segmentId,
    });

    setCampaignValueAdjustments(newData.segment.campaignValueAdjustments || []);
    setEditCampaignValueAdjustments(isEmpty(newData.segment.campaignValueAdjustments) ? [{
      date: '',
      spend: 0,
      changed: false,
    }] : newData.segment.campaignValueAdjustments);

    if (newData.segment && newData.segment.spotSelectionStrategy === 'spots-per-hour') {
      // if the segment is spots per hour, default to the spot based view
      setCurrentView('spot');
    }

    setData({
      ...data,
      ...newData,
    });
  };

  /**
   * Get Order Type
   */
  const getOrderTypeItem = () => {
    return find(OrderTypeHelper.list(), { value: segment.orderType }) || {};
  };

  /**
   * Get the page content
   */
  const getPageContent = () => {
    switch (type) {
      case 'allocations':
        return (
          <OrderSegmentAllocationOverview
            orderType={segment.orderType}
            orderId={orderId}
            segmentId={segmentId}
            currencyCode={segment.currencyCode}
          />
        );

      case 'overview':
      default:
        return (
          <>
            <VibeAccordion
              style={{
                margin: '0 2rem',
                marginBottom: '2rem',
              }}
              title="Segment Information"
              expanded={accordionExpanded}
              onChange={onChangeAccordion}
            >
              <SegmentInformation
                segmentType={segment.segmentType}
                mediaFormat={segment.mediaFormat}
                spotType={segment.spotType}
                spotSelectionStrategy={segment.spotSelectionStrategy}
                frequencyCapped={segment.frequencyCapped}
                frequencyCap={segment.frequencyCap}
                allowBackToBackAdDelivery={segment.allowBackToBackAdDelivery}
                allocateRemainder={segment.allocateRemainder}
                onChangeCampaign={onChangeCampaign}
                campaignValueAdjustments={campaignValueAdjustments}
                editCampaignValueAdjustments={editCampaignValueAdjustments}
                currencyCode={segment.currencyCode}
                segmentStartDate={segment.startDate}
                segmentEndDate={segment.endDate}
                APISavingError={APISavingError}
                onRemoveRow={onRemoveRow}
              />
            </VibeAccordion>

            {dayParts.length > 0 && !isViewOnly && (
              <OrderCreatives
                segment={segment}
                orderId={orderId}
                segmentId={segmentId}
                updateDayPartsNew={updateDayPartsNew}
                allowCreativeManagement={segment.adProvider === 'vibenomics'}
              />
            )}

            {!isViewOnly && (
              <div
                style={{
                  padding: '2rem',
                }}
              >
                <Grid spacing={2} container>
                  <Grid xs={6} item>
                    <Notes
                      notes={segment?.activity?.map((item) => {
                        return {
                          _id: item._id,
                          type: item.activityType,
                          userId: item.userId,
                          username: `${item.userFName} ${item.userLName}`,
                          date: item.date,
                          avatarUrl: item.userImageUrl,
                          note: item.note,
                        };
                      })}
                      allowCreate={user.can('order_segment.modify')}
                      numVisibleMessages={5}
                      onCreate={onCreateNote}
                    />
                  </Grid>
                </Grid>
              </div>
            )}
            <div
              style={{
                position: 'sticky',
                display: 'flex',
                alignItems: 'center',
                bottom: 0,
                height: 32,
                padding: '8px 24px',
                borderTop: `1px solid ${color.whiteSmoke}`,
                backgroundColor: color.white,
                zIndex: 10,
              }}
            >
              <VibeButton
                text="Save Changes"
                color="primary"
                disabled={(isDisabled || isNewDisabled || !campaignChanged) && !currentFieldsChanged}
                onClick={onSave}
              />
            </div>

            <VibeModal
              show={showDeleteModal}
              type="confirm"
              confirmProps={{
                color: 'error',
              }}
              title="You are deleting an Adjustment Row"
              text={(
                <div>
                  This cannot be undone. Do you wish to continue?
                </div>
              )}
              onConfirm={onConfirmDeleteCampaignRow}
              onClose={onCloseDeleteCampaignRow}
            />
          </>
        );
    }
  };

  /**
   * Create a Note
   */
  const onCreateNote = async (note, onSuccess, onError) => {
    try {
      const response = await API.Order.Segment.addNote({
        _id: orderId,
        segmentId,
        note,
      });

      const success = get(response, '[0].type') === 'ORDERSEGMENT.NOTE_ADDED';

      if (success) {
        getData();
        onSuccess();
      } else {
        onError('Error Creating Note');
      }
    } catch (err) {
      // error adding the note
      onError(err.message);
    }
  };

  const updateDayPartsNew = async (dayParts) => {
    const response = await API.Order.Segment.updateDayParts({
      _id: orderId,
      segmentId,
      dayParts,
    });

    await getData();

    const successText = 'ORDERSEGMENT.CREATIVES_UPDATED';
    const success = get(response, '[0].type') === successText;

    if (success) {
      queueToast({
        type: 'success',
        title: 'Saved...',
        allowClose: true,
      });
      setPanel({
        show: false,
        children: null,
      });
    }
  };

  const onChangeCampaign = (type, data, row) => {
    setAPISavingError(false);
    switch (type) {
      case 'add-row':
        setEditCampaignValueAdjustments(
          [
            ...editCampaignValueAdjustments,
            {
              date: '',
              spend: 0,
              changed: true,
            },
          ],
        );
        setCampaignChanged(true);
        break;
      case 'remove-row':
        setEditCampaignValueAdjustments(editCampaignValueAdjustments.filter((valueAdj, i) => {
          if (i !== row) {
            return true;
          }
          return false;
        }));
        break;
      default:
        setEditCampaignValueAdjustments(editCampaignValueAdjustments.map((valueAdj, i) => {
          if (i === row) {
            if (type === 'date') {
              valueAdj.date = data;
            }
            if (type === 'spend') {
              valueAdj.spend = data.value;
            }
            valueAdj.changed = true;
          }
          return valueAdj;
        }));
        break;
    }
  };

  /**
  * Save the category
  */
  const onSave = async (campaignValueAdjustmentsSave) => {
    queueToast({
      type: 'info',
      title: 'Saving...',
      allowClose: true,
    });

    try {
      const response = await API.Order.Segment.campaignValueAdjustments({
        _id: orderId,
        segmentId,
        campaignValueAdjustments: campaignValueAdjustmentsSave.type
          ? editCampaignValueAdjustments : campaignValueAdjustmentsSave,
      });

      const responseType = get(response, '[0].type');

      const success = responseType === 'ORDERSEGMENT.CAMPAIGN_VALUE_ADJUSTMENTS_UPDATED';

      if (success) {
        // Successfully saved the baseline
        const saveMessage = 'Campaign Value Adjustments Updated!';

        queueToast({
          type: 'success',
          title: saveMessage,
          allowClose: true,
          delay: 500,
        });

        setCampaignChanged(false);
        getData();
      }
    } catch (err) {
      queueToast({
        type: 'error',
        title: 'Campaign Value Adjustments Error',
        allowClose: true,
        delay: 500,
      });
      setAPISavingError(true);
    }
  };

  /**
   * Renove a Campaign Value Row
   */
  const onRemoveRow = (i) => {
    setAPISavingError(false);
    setCampaignChanged(false);
    if (i < campaignValueAdjustments.length) {
      setRowToDelete(i);
      setShowDeleteModal(true);
    } else {
      setEditCampaignValueAdjustments(editCampaignValueAdjustments.filter((cva, j) => i !== j));
    }
  };

  /**
   * Click Delete External ID in confirm modal
   */
  const onConfirmDeleteCampaignRow = async () => {
    const updatedCampaignRows = campaignValueAdjustments.filter((cva, i) => {
      if (i === rowToDelete) {
        return false;
      }
      return true;
    });

    onSave(updatedCampaignRows);
    setShowDeleteModal(false);
  };

  /**
   * Close Delete modal
   */
  const onCloseDeleteCampaignRow = () => {
    setShowDeleteModal(false);
  };

  /*
   * When the user clicks on the impression values button
   */
  const onClickImpressionValues = () => {
    setCurrentView('impression');
  };

  /**
   * When the user clicks on the spot values button
   */
  const onClickSpotValues = () => {
    setCurrentView('spot');
  };

  /**
   * Export locations to CSV file
   */
  const onExport = async () => {
    const segmentExpandedLocations = await API.Order.Segment.getExpandedLocations({
      _id: orderId,
      segmentId,
    }, {
      headers: {
        Accept: 'text/csv',
      },
    });

    const blob = new Blob([segmentExpandedLocations], { type: 'text/csv;charset=utf-8' });
    const timestamp = moment().format('YYYY-MM-DD_HH-mm-ss');
    saveAs(blob, `locations-${timestamp}.csv`);
  };

  // Refresh segment data if segmentId changes
  useEffect(() => {
    getData();
  }, [segmentId, orderId]);

  if (!segment) {
    return null;
  }

  const orderTypeItem = getOrderTypeItem();
  const breadcrumbs = (segment?.orderName && segment?.name) && [
    {
      title: 'Dashboard',
      linkUrl: '../../orders/dashboard',
    },
    {
      title: updateTitle(segment?.orderStatus),
      linkUrl: `../../orders/dashboard/${segment?.orderStatus}`,
    },
    {
      title: segment.orderName,
      icon: orderTypeItem.icon,
      iconTooltip: orderTypeItem.label,
      iconColor: color.manatee,
      linkUrl: '../details',
    },
    {
      title: segment.name,
      linkUrl: './',
    },
  ];

  // Get segment day parts for the earliest start and latest end time
  const dayParts = get(segment, 'dayParts', []) || [];

  // Sort the day parts to make sure we get the correct start / stop time
  const dayPartSortStart = sortBy(dayParts, 'startTime');
  const dayPartSortEnd = sortBy(dayParts, 'endTime');

  // Get the earliest start time and the latest end time from the day parts
  const earliestStartTime = get(head(dayPartSortStart), 'startTime', 'Unknown');
  const latestEndTime = get(last(dayPartSortEnd), 'endTime', 'Unknown');

  const isViewOnly = segment.partnerAccessLevel === 'view-all'
    || segment.partnerAccessLevel === 'view-limited'
    || segment.partnerAccessLevel === 'none';

  const isDisabled = campaignValueAdjustments.some(cva => isEmpty(cva.date)
    || (isNaN(cva.spend) || cva.spend < 1));
  const isNewDisabled = editCampaignValueAdjustments.some(cva => isEmpty(cva.date)
    || (isNaN(cva.spend) || cva.spend < 1));
  const currentFieldsChanged = editCampaignValueAdjustments.some(cva => cva.changed);

  return (
    <div
      className="OrderSegment"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'auto',
      }}
    >
      <SubNavigation
        breadcrumbs={breadcrumbs}
      />

      <div className="segment-header">
        <div
          style={{
            flexGrow: 1,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <div className="segment-title">
              Order Detail Summary
            </div>

            {isViewOnly && (
              <Tag
                tag={{
                  name: 'View-Only',
                  icon: viEye,
                }}
                style={{
                  background: color.primary16,
                  color: color.primary,
                  fontSize: 12,
                }}
              />
            )}
          </div>

          <div className="segment-stats">
            <div className="stat">
              <div>Companies</div>
              <div className="value">{segment?.locationSummary?.companies || 0}</div>
            </div>

            <div className="stat">
              <div>Banners</div>
              <div className="value">{segment?.locationSummary?.banners || 0}</div>
            </div>

            <div className="stat">
              <div>Locations</div>
              <div className="value">{segment?.locationSummary?.locations || 0}</div>
            </div>

            <VibeButton
              key="btn-export"
              variant="outlined"
              text="Export"
              color="secondary"
              icon={(
                <VibeIcon
                  icon={viDownload}
                  color={color.primary}
                  size={16}
                />
              )}
              tooltip="Export Locations to CSV"
              tooltipProps={{
                placement: 'top',
              }}
              onClick={onExport}
            />
          </div>
        </div>

        <SegmentSchedule
          startDate={segment.startDate}
          endDate={segment.endDate}
          startTime={earliestStartTime}
          endTime={latestEndTime}
          days={segment.daysOfWeek || []}
        />
      </div>

      {segment.reportingAuthority === 'vibenomics' && (
        <>
          <div
            style={{
              margin: '0 2rem',
            }}
          >
            <ButtonGroup variant="outlined" color="inherit">
              <BootstrapButton
                variant={isImpression
                  ? 'contained'
                  : 'outlined'}
                disableElevation
                onClick={onClickImpressionValues}
              >
                Impression Values
              </BootstrapButton>

              <BootstrapButton
                variant={!isImpression
                  ? 'contained'
                  : 'outlined'}
                disableElevation
                onClick={onClickSpotValues}
              >
                Spot Values
              </BootstrapButton>
            </ButtonGroup>
          </div>

          <InfoCards
            style={{
              margin: '1rem 2rem',
            }}
            data={segment}
            currentView={currentView}
          />
        </>
      )}

      <Submenu
        rootUrl={`/ad-ops/order/${orderId}/segment/${segmentId}`}
        matchParameter="tab"
        items={[
          {
            name: 'Overview',
            label: 'Segment Overview',
            show: true,
            enabled: user.can('order_segment.view'),
          },
          {
            name: 'Allocations',
            label: segment.orderType === 'always-on-pmp'
              ? 'Segment Delivery'
              : 'Segment Allocations',
            icon: isArray(segment.warnings) && segment?.warnings?.length > 0 ? (
              <VibeAlert
                severity="warning"
                alerts={segment.warnings || []}
                size={24}
                iconOnly
              />
            ) : null,
            show: true,
            enabled: user.can('order_segment.view'),
          },
        ]}
      />

      <div
        style={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          paddingBottom: 0,
          marginTop: '2rem',
        }}
      >
        {getPageContent()}
      </div>
    </div>
  );
}

function mapStateToProps(state) {
  return {
    user: state.login.user,
  };
}

const mapDispatchToProps = {
  queueToast: ToastActions.queueToast,
  setPanel: GlobalActions.setPanel,
};

export default connect(mapStateToProps, mapDispatchToProps)(OrderSegment);
